import React, { useState } from "react";
import TeamHeader from "../TeamHeader/TeamHeader";
import MainHub from "../MainHub/MainHub";

// Assets
import tempFlow from "./assets/hardware-flow.png";
import aiFlow from "./assets/ai-flow.png";
import serverFlow from "./assets/Server Pipeline (Level 2) - Page 1 Frame 3.png";
import toaFlow from "./assets/toa-flow.png";
import gisFlow from "./assets/gis-flow.png";
import marketingFlow from "./assets/marketing-flow.png";
import manufactuingFlow from "./assets/manufacturing-flow.png";

// Icons
import { RiArrowDownSFill, RiArrowUpSFill } from "react-icons/ri";

const MainHubPage = () => {
  const [selectedRegions, setSelectedRegions] = useState<string[]>(["All"]);
  const [openSlips, setOpenSlips] = useState<string[]>([]); // ← allow multiple

  const toggleSlip = (key: string) => {
    setOpenSlips((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Placeholder stats (unchanged)
  const placeholderStats = {
    strikes: {
      cloudToGround: 36900,
      detectionEfficiency: 80,
      locationAccuracy: 90,
    },
    fires: {
      lightningIgnited: 364,
      lccPercentage: 80,
      lccRatio: 30.4,
      hrlRatingMedian: 24,
    },
  };

  // Later fetched from Django
  const flowSections: {
    [key: string]: {
      title: string;
      paragraphs: string[];
      image: string;
    };
  } = {
    hardware: {
      title: "Hardware",
      paragraphs: [
        "The Hardware team is at the core of the FNN, focusing on the detectors. They are responsible for four key areas: developing, improving, maintaining, and monitoring all detectors installed worldwide.",
        "The development team seeks innovative solutions to enhance the robustness and stability of the detectors, creating new firmware and hardware for the upcoming season. Through continuous monitoring, all reported failures are documented in detail. Once consolidated, the hardware team works to improve the system, including necessary firmware updates. Each firmware update is implemented only after a rigorous test bench run, ensuring that the latest firmware is ready for deployment in the field.",
        "If a detector's status changes to offline, an alert is triggered, prompting a maintenance team to address the issue. Once the detector is back online, a maintenance report is generated and analyzed by the development team. If the issue is chronic, it will be considered for the next version of the hardware.",
        "The monitoring system is crucial, providing real-time information on the current health of each detector. It allows for tracking various parameters, such as files transferred per hour, running algorithms, and versions of both hardware and software.",
      ],
      image: tempFlow,
    },
    server: {
      title: "Server",
      paragraphs: [
        "The Server Team is responsible for real-time analysis within the Florida pipeline, focusing on processing HF and LF bin files alongside peak results from edge stations. The initial phase involves reading and preprocessing the bin files to identify prominent peaks in the HF files, capturing both time and amplitude in a data frame.",
        "For the LF files, two processing versions operate in parallel: one that utilizes LS Matching logic without strokes and another that incorporates stroke data for LCC production while filtering LF bins.",
        "This foundational work sets the stage for further analysis and aggregation of peak data. In the subsequent phase, the Post Process script aggregates peaks from CSV results within a 1ms window, selecting the most significant peaks for submission to a solver function. The LS Match component then refines these results by eliminating invalid locations and matching independent LCCs based on time checks.",
        "This process culminates in the preparation of a final data frame that is submitted to the research database. The entire workflow is executed every 10 minutes via Cronjob, with a built-in 5-minute delay to ensure real-time results are accurately captured and processed.",
      ],
      image: serverFlow,
    },
    toa: {
      title: "TOA",
      paragraphs: [
        "The Detection and Location (ToA) Team develops and maintains the core algorithms that form the foundation of the FNN lightning detection pipeline. Operating at the interface between the detectors and the Server Team’s real-time system, we ensure accurate event detection and precise geolocation of lightning discharges. ",
        "At the real-time level, the team’s algorithms are delivered to Server to handle the full processing chain: Peak Detection (for high-frequency electric field signals), LCC Detection (for low-frequency electric field analysis), Cluster (time-match peaks across multiple stations), Solver (responsible for calculating lightning locations using Time of Arrival data), and LS Match (co-developed with the Server Team to integrate all results submitted to the server pipeline).",
        "For offline analysis, the team develops and operates advanced tools to evaluate and refine detection performance. These include: FNN Plot-Bin (for waveform visualization and preprocessing), the ToA Dashboard (for assessing station detection metrics and algorithm performance in location efficiency and accuracy), and FNN Monte Carlo (for simulating network behavior using real station capabilities). Together, these tools support both real-time operations and long-term improvements to detection algorithms in collaboration mostly with Hardware and Server teams.",
      ],
      image: toaFlow,
    },
    ai: {
      title: "AI",
      paragraphs: [
        "The AI Team is responsible for maintaining the database, data pipelines, and AI models. Their goal is to process and deliver data received from the Server Team to both the Customers and the GIS Team through API endpoints.",
        "In addition to managing data flow, the team analyses the historical datasets, identifies and integrates new data sources, and improves model performance. The model is trained on environmental and strike-related data to estimate the severity of lightning strikes. This output is known as High-Risk Lightning (HRL) index. It shows the relative likelihood that a lightning strike will ignite a fire.",
      ],
      image: aiFlow,
    },
    gis: {
      title: "GIS",
      paragraphs: [
        "The GIS (Geographic Information Systems) Team specializes in spatial data analysis and visualization to support decision-making and operational awareness. Our core responsibility is the development and maintenance of interactive dashboards within the ESRI environment, focusing on real-time and historical lightning and fire incident tracking.",
        "We collaborate closely with the Machine Learning/AI team, which provides lightning data—including geolocation, High-Risk-Lightning (HRL) value, and potential impact on fire ignition and electrical infrastructure. This data is embedded with key environmental attributes such as the Keetch-Byram Drought Index (KBDI), Energy Release Component (ERC), and Precipitation metrics.",
        "By integrating geospatial intelligence with dynamic mapping tools and remote sensing products, we provide stakeholders with timely, actionable insights that enhance emergency response, resource allocation, and strategic planning.",
      ],
      image: gisFlow,
    },
    marketing: {
      title: "Marketing",
      paragraphs: [
        "The Marketing Team drives FNN’s storytelling, outreach, and brand visibility across utilities, government agencies, and climate resilience sectors. Our core responsibility is translating complex technical capabilities—such as High-Risk-Lightning (HRL:tm:) detection, edge-AI analysis, and Wildfire Hub intelligence—into clear, impactful narratives that resonate with diverse stakeholders.",
        "We collaborate closely with the GIS and AI teams to transform data insights into visual, data-driven communications, including case studies, campaign materials, and multimedia content. The team also manages all product messaging and pricing communications, including the Starter, Pro, and Enterprise plan details that define FNN’s service tiers and customer engagement models.",
      ],
      image: marketingFlow,
    },
    manufacturing: {
      title: "Manufacturing",
      paragraphs: [
         "The Manufacturing Team for the building block of FNN. This is where the designs by the hardware team get built both as samples and also in larger batch manufacturing (e.g. the Manufacturing Team collaborated closely with the Hardware Team for the making of two sample 2026 detectors). Once the Hardware Team approves the samples, then the Manufacturing Team assures that everything can be scaled keeping at the forefront: flexibility, cost, rate, and quality. The Manufacturing Team is furthermore also directly responsible for proper inventory management, and collaborates with the Business Development Team on assuring that there are always enough detectors to satisfy customer demand.",
         "The Manufacturing Team coordinates the preparation of drawings, 3d models, samples, bill of materials lists, and  SOPs with the Hardware Team to make sure that the Manufacturing Team can work efficiently with external suppliers to meet the needs of the Hardware Team. Once there is feedback received from either the Hardware Team or customers about the detectors, the Manufacturing Team is responsible for assuring that these suggested changes can be manufactured properly.",
         "In summary, the Manufacturing Team's main goal is to scale production capabilities and find the right balance between continuous development and documentation & implementation of these changes to assure that the detectors perform optimally once deployed.",
      ],
      image: manufactuingFlow,
    },
  };

  const formatRegionName = (name: string) =>
    name
      .replace(/^detectors_/, "")
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  return (
    <div className="main-hub-wrapper">
      <TeamHeader />
      <MainHub onRegionChange={setSelectedRegions} />

      <div className="rep-wrapper">
        {/* Strikes Report */}
        <div className="main-hub-report">
          <h1>Strikes</h1>
          {selectedRegions.map((region) => (
            <div className="main-hub-lf-card" key={region}>
              <h1>{formatRegionName(region)}</h1>
              <div className="main-hub-lf-stats">
                <div className="main-hub-lf-stat">
                  <p>Cloud-to-Ground</p>
                  <h2>{placeholderStats.strikes.cloudToGround}</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>Detection Efficiency</p>
                  <h2>{placeholderStats.strikes.detectionEfficiency}%</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>Location Accuracy</p>
                  <h2>{placeholderStats.strikes.locationAccuracy}%</h2>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Fires Report */}
        <div className="main-hub-report">
          <h1>Fires</h1>
          {selectedRegions.map((region) => (
            <div className="main-hub-lf-card" key={region}>
              <h1>{formatRegionName(region)}</h1>
              <div className="main-hub-lf-stats">
                <div className="main-hub-lf-stat">
                  <p>Lightning Ignited</p>
                  <h2>{placeholderStats.fires.lightningIgnited}</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>LCC Percentage</p>
                  <h2>{placeholderStats.fires.lccPercentage}%</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>LCC Ratio</p>
                  <h2>{placeholderStats.fires.lccRatio}</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>HRL Rating Median</p>
                  <h2>{placeholderStats.fires.hrlRatingMedian}%</h2>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FLOW SECTION */}
      <div className="main-flow-container">

        <h1 className="cutomer-title" style={{marginBottom:'0.5rem', marginTop:'1.5rem'}}>Pipelines</h1>

        {Object.entries(flowSections).map(([key, section]) => {
          const isOpen = openSlips.includes(key);
          return (
            <div key={key} style={{ width: "100%" }}>
              <div
                className={`main-flow-slip ${isOpen ? "active" : ""}`}
                onClick={() => toggleSlip(key)}
              >
                <div className="main-flow-slip-header">
                  <span>{section.title}</span>
                  {isOpen ? (
                    <RiArrowDownSFill size={20} className="flow-arrow-icon" />
                  ) : (
                    <RiArrowDownSFill size={20} className="flow-arrow-icon" />
                  )}
                </div>
              </div>

              {isOpen && (
                <div className="main-flow-slip-opened-content">
                  <div className="main-flow-slip-opened-content-left">
                    {section.paragraphs.map((text, idx) => (
                      <p key={idx}>{text}</p>
                    ))}
                  </div>
                  <div className="main-flow-slip-opened-content-right">
                    <img src={section.image} alt={`${section.title} flow`} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MainHubPage;
